import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaSearch } from "react-icons/fa";
import { DatePicker, Space } from "antd";
import dayjs from "dayjs";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import placeholderImage from "../../../images/placeholder.png";
import useFetch from "../../../components/shared/useFetch";
import EmployeeView from "./EmployeeView";

const { RangePicker } = DatePicker;

const EmployeeReportsChild = () => {
  const navigate = useNavigate();
  const [{ errorLogApi }] = useAtom(gbState);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [employeeData, setEmployeeData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateValue, setDateValue] = useState(null);
  const [dateInterval, setDateInterval] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const urlLink = `${process.env.REACT_APP_API_URL}/employee/`;
  const { data, loading, error, refetch } = useFetch(urlLink);

  useEffect(() => {
    if (data?.employees) {
      const updatedData = data.employees.map((employee) => ({
        ...employee,
        id: employee._id,
        name: `${employee.first_name} ${employee.last_name}`,
      }));
      setEmployeeData(updatedData);
      setUnfilteredData(updatedData);
    }
  }, [data]);

  useEffect(() => {
    if (dateValue) {
      setDateInterval({
        sd: dayjs(dateValue[0]).valueOf(),
        ed: dayjs(dateValue[1]).valueOf(),
      });
    }
  }, [dateValue]);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const searchRecordsBetweenDates = () => {
    if (dateInterval.sd && dateInterval.ed) {
      const filteredData = unfilteredData.filter((employee) => {
        const createdTime = dayjs(employee.createdAt).valueOf();
        return dateInterval.sd <= createdTime && createdTime <= dateInterval.ed;
      });
      setEmployeeData(filteredData);
      setFilteredData(filteredData);
    }
  };

  const resetData = () => {
    setDateValue(null);
    setDateInterval({});
    setFilteredData([]);
    setEmployeeData(unfilteredData);
    setSearchTerm("");
  };

  const filteredEmployees = useMemo(() => {
    return employeeData.filter((employee) =>
      Object.values(employee).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [employeeData, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredEmployees.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredEmployees, page, rowsPerPage]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <h1 className="mb-6 text-2xl font-bold">Employee Reporting</h1>

      <div className="flex flex-wrap items-center mb-4 space-y-2 sm:space-y-0 sm:space-x-4">
        <Space direction="vertical" size={12}>
          <RangePicker
            value={dateValue}
            onChange={(val) => setDateValue(val)}
            format="MM-DD-YYYY"
            placeholder={["Start Date", "End Date"]}
          />
        </Space>
        <Button
          variant="contained"
          color="primary"
          onClick={searchRecordsBetweenDates}
          disabled={!dateInterval.sd || !dateInterval.ed}
        >
          Search
        </Button>
        <Button variant="outlined" onClick={resetData}>
          Reset Data
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search employees..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Designation</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Date of Birth</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Tooltip title="View details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        toggleModal();
                        setCurrentRecord(row);
                      }}
                    >
                      <FaEye className="text-blue-600" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <img
                    src={
                      row.image
                        ? `${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/employee/${row.image}`
                        : placeholderImage
                    }
                    alt="Employee"
                    className="w-10 h-10 rounded-full"
                    crossOrigin="anonymous"
                  />
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.designation}</TableCell>
                <TableCell>{row.mobile}</TableCell>
                <TableCell>{row.gender}</TableCell>
                <TableCell>
                  {row.date_of_birth
                    ? dayjs(row.date_of_birth).format("MM-DD-YYYY")
                    : ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <ErrorBoundaryRoutes route="/employees" page="ModalView">
        {showModal && (
          <Modal onClose={toggleModal}>
            <h2 className="mb-4 text-xl font-bold">Employee Detail</h2>
            <EmployeeView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default EmployeeReportsChild;
